"use client"

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid"
import { ArrowUturnLeftIcon } from "@heroicons/react/20/solid"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { useMachineStageProgress } from "@src/components/DefuseSDK/features/common/useMachineStageProgress"
import {
  SWAP_STAGES,
  SWAP_STAGE_LABELS,
  type SwapStage,
  getStageFromState,
  is1csError,
  is1csSuccess,
  isIntentError,
  isIntentSuccess,
} from "@src/components/DefuseSDK/features/swap/utils/swapStatusUtils"
import {
  formatTokenValue,
  formatUsdAmount,
} from "@src/components/DefuseSDK/utils/format"
import { ProgressSteps } from "@src/components/ProgressIndicator"
import { ArrowDownIcon } from "@src/icons"
import type { TrackedSwapIntent } from "@src/providers/SwapTrackerMachineProvider"
import Button from "./Button"
import { INTENTS_EXPLORER_URL } from "./DefuseSDK/constants/blockchains"
import { useTokensUsdPrices } from "./DefuseSDK/hooks/useTokensUsdPrices"
import getTokenUsdPrice from "./DefuseSDK/utils/getTokenUsdPrice"
import SwapPageHeader from "./SwapPageHeader"

type SwapStatusProps = {
  swap: TrackedSwapIntent
  variant: "full" | "dock"
  onSwapAgain?: () => void
}

export function SwapStatus({ swap, variant, onSwapAgain }: SwapStatusProps) {
  const { displayStage, displayIndex, canRetry, stateValue, contextStatus } =
    useMachineStageProgress({
      actorRef: swap.actorRef,
      stages: SWAP_STAGES,
      getStageFromState,
    })

  const isError = swap.is1cs
    ? is1csError(contextStatus)
    : isIntentError(stateValue)

  const isSuccess = swap.is1cs
    ? is1csSuccess(contextStatus)
    : isIntentSuccess(stateValue)

  if (variant === "dock") {
    return (
      <ProgressSteps
        stages={SWAP_STAGES}
        stageLabels={SWAP_STAGE_LABELS}
        displayStage={displayStage}
        displayIndex={displayIndex}
        isError={isError}
        isSuccess={isSuccess}
        size="sm"
      />
    )
  }

  return (
    <FullView
      swap={swap}
      depositAddress={swap.depositAddress}
      displayStage={displayStage}
      displayIndex={displayIndex}
      isError={isError}
      canRetry={canRetry}
      isSuccess={isSuccess}
      onSwapAgain={onSwapAgain}
    />
  )
}

function FullView({
  swap,
  depositAddress,
  displayStage,
  displayIndex,
  isError,
  canRetry,
  isSuccess,
  onSwapAgain,
}: {
  swap: TrackedSwapIntent
  depositAddress: string | undefined
  displayStage: SwapStage
  displayIndex: number
  isError: boolean
  canRetry: boolean
  isSuccess: boolean
  onSwapAgain?: () => void
}) {
  const { tokenIn, tokenOut, totalAmountIn, totalAmountOut } = swap
  const { data: tokensUsdPriceData } = useTokensUsdPrices()

  const formattedAmountIn = formatTokenValue(
    totalAmountIn.amount,
    totalAmountIn.decimals,
    { min: 0.0001, fractionDigits: 4 }
  )
  const formattedAmountOut = formatTokenValue(
    totalAmountOut.amount,
    totalAmountOut.decimals,
    { min: 0.0001, fractionDigits: 4 }
  )

  const usdAmountIn = getTokenUsdPrice(
    formattedAmountIn,
    tokenIn,
    tokensUsdPriceData
  )
  const usdAmountOut = getTokenUsdPrice(
    formattedAmountOut,
    tokenOut,
    tokensUsdPriceData
  )

  const explorerUrl = depositAddress
    ? `${INTENTS_EXPLORER_URL}/transactions/${depositAddress}`
    : null

  return (
    <>
      <SwapPageHeader />

      <div className="mt-5 rounded-3xl border border-gray-200 bg-white p-5 sm:mt-7 sm:p-6 dark:border-white/5 dark:bg-gray-800">
        <div className="flex flex-col">
          <div className="flex items-center gap-3">
            <AssetComboIcon icon={tokenIn?.icon} sizeClassName="size-11" />
            <div>
              <div className="font-bold text-2xl text-gray-900 leading-7 tracking-tight dark:text-white">
                {formattedAmountIn} {tokenIn.symbol}
              </div>
              <div className="font-medium text-base/5 text-gray-500 dark:text-gray-400">
                {formatUsdAmount(usdAmountIn ?? 0)}
              </div>
            </div>
          </div>

          <div
            className="flex h-16 w-11 items-center justify-center"
            aria-hidden
          >
            <ArrowDownIcon className="size-7 text-gray-400" />
          </div>

          <div className="flex items-center gap-3">
            <AssetComboIcon icon={tokenOut?.icon} sizeClassName="size-11" />
            <div>
              <div className="font-bold text-2xl text-gray-900 leading-7 tracking-tight dark:text-white">
                {formattedAmountOut} {tokenOut.symbol}
              </div>
              <div className="font-medium text-base/5 text-gray-500 dark:text-gray-400">
                {formatUsdAmount(usdAmountOut ?? 0)}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-gray-200 border-t pt-6 sm:mt-8 sm:pt-8 dark:border-white/10">
          <ProgressSteps
            stages={SWAP_STAGES}
            stageLabels={SWAP_STAGE_LABELS}
            displayStage={displayStage}
            displayIndex={displayIndex}
            isError={isError}
            isSuccess={isSuccess}
            size="md"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {explorerUrl && !swap.isConfidential && (
          <Button
            href={explorerUrl}
            variant="secondary"
            size="xl"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
          >
            View on explorer
            <ArrowTopRightOnSquareIcon className="size-4" />
          </Button>
        )}
        {isSuccess && (
          <Button size="xl" fullWidth onClick={onSwapAgain}>
            <ArrowUturnLeftIcon className="size-4.5 shrink-0" />
            Swap again
          </Button>
        )}
        {isError && canRetry && (
          <Button
            size="xl"
            fullWidth
            onClick={() => swap.actorRef.send({ type: "RETRY" })}
          >
            Retry
          </Button>
        )}
        {isError && (
          <Button size="xl" variant="secondary" fullWidth onClick={onSwapAgain}>
            Try a new swap
          </Button>
        )}
      </div>
    </>
  )
}
